(ns arthur.domain.clip
  "A CLIP: the unit of work, and a library of symbols.

      {:name      \"take\"
       :fps       30
       :width 320 :height 200
       :analyses  {analysis-id {...}}
       :subjects {...} :features {...} :groups {...}
       :symbols {:main {:id :main :frames 229 :nodes {...}}}}

  Every field here is a fact about the clip and NOT about a bag of nodes, which is
  the cut this namespace exists to make. Before it, one map carried both: `:fps`,
  the stage dimensions, the analysis record and the tracking identities sat beside
  `:nodes`. The cost of leaving them together was not untidiness. It was that a
  SYMBOL had nowhere to live: a symbol is a bag of nodes with a frame space and
  nothing else, so under the old shape it would have had to be a clip with seven
  meaningless fields.

  Now there is one node-holding type — `arthur.domain.symbol` — and a clip holds
  a MAP of them. A `:kind :instance` node places one symbol inside another, and
  the clip resolver gives each instance its own reading heads.

  WHAT IS NOT HERE: how nested symbols' frames and coordinates relate, and
  moving nodes between them, are `arthur.domain.nest`; bringing symbols in from
  another clip is `arthur.domain.bring`. This namespace is the document and the
  operations that only need the document.

  NO SYMBOL IS SPECIAL. There is no reserved root id: which symbol is on screen
  is the editor's state, not the document's, and every function here that needs
  a symbol is told which. A new document has one symbol called `:main` because
  it has to be called something, and that is all the name means — it can be
  renamed or placed inside another symbol like any of them. What the document
  does say is `:root`, which symbol it opens on: a pointer, not a kind of
  symbol, the way a Flash file names its scene. See `opens-on` for why that
  cannot be worked out instead.

  :fps is the document's one rate. A symbol has none: it is a bag of frames, and
  each of them plays for one tick of the document's clock, as in Flash. See
  `docs/time.md`."
  (:refer-clojure :exclude [symbol])
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.feature :as feature]
            [arthur.domain.node :as node]
            [arthur.domain.palette :as pal]
            [arthur.domain.pose :as pose]
            [arthur.domain.symbol :as symbol]))

(def clip-keys
  "Every top-level field of a clip, and the reason `arthur.domain.leaf` refuses
  one it does not know: a field added to the clip without a leaf to save it in is
  a field that saves silently and comes back missing. The failure is a document
  that loses something on every round trip, which is the one bug a persistence
  layer must not be able to have. Add the field here and to `leaf/leaves` and
  `leaf/clip` in the same commit."
  #{:name :fps :analyses :subjects :features :groups :width :height :symbols
    :palettes :default-palette :root})

(defn symbol
  "One of the clip's symbols, by id."
  [clip sid]
  (get-in clip [:symbols sid]))

(defn trace?
  "Is `sym` a tracing symbol: footage or a still to draw over, which is placed and
  moved like any symbol and never drawn into the picture? See `trace-op`."
  [sym]
  (= :trace (:type sym)))

(defonce ^:private placed-cache (atom nil))

(defn placements
  "Where every symbol is placed: `{sid [[owner frame] ...]}`, earliest first,
  where `owner` is the symbol whose node places it and `frame` is where in the
  owner that node starts.

  Cached by IDENTITY of the document, as `raster` caches a palette: a document
  is a value, so the same object means the same answer, and names and the pool
  ask this once per symbol per render."
  [clip]
  (let [cached @placed-cache]
    (if (identical? clip (:clip cached))
      (:index cached)
      (let [index (-> (reduce (fn [m [owner sym]]
                                (reduce (fn [m [_ n]]
                                          (if-let [sid (node/source n)]
                                            (update m sid (fnil conj [])
                                                    [owner (:at (node/time-of n))])
                                            m))
                                        m (:nodes sym)))
                              {} (:symbols clip))
                      (update-vals #(vec (sort-by second %))))]
        (reset! placed-cache {:clip clip :index index})
        index))))

(defn owners
  "The symbols that place `sid`, each once."
  [clip sid]
  (into [] (comp (map first) (distinct)) (get (placements clip) sid)))

(defn symbol-name
  "What to call a symbol: its `:name`, or where it is when it has none.

  A DRAWING IS NAMED BY WHERE IT IS DRAWN. One placed by exactly one symbol
  reads as that symbol's name and the frame it starts on — `body 12` — worked
  out on every read, so renaming the lane renames its drawings and moving one
  renames it. A symbol placed nowhere, or in two places, has no one place to be
  named by and reads as its id."
  ([clip sid] (symbol-name clip sid #{}))
  ([clip sid seen]
   (let [given (:name (symbol clip sid))
         [owner :as os] (owners clip sid)]
     (cond
       given given
       (and (= 1 (count os)) (not (seen owner)))
       (str (symbol-name clip owner (conj seen sid)) " "
            (js/Math.floor (second (first (get (placements clip) sid)))))
       :else (name sid)))))

(defn node-label
  "What to call node `n` on screen.

  AN INSTANCE IS CALLED WHAT IT PLACES, read through on every render, and has
  no name of its own. A name on the placement meant the same drawing could show
  under two names, and that a rename in one place reached nowhere else; renaming
  an instance renames its symbol (`::ui/rename-node`), so every placement of it
  follows. How many places share it is `uses`.

  Anything else is called by its own `:name`. An id is a uuid for a placement
  and a keyword for an authored node, and neither reads as a name, so the last
  resort is a legible stand-in — `:face-1` loses its colon and a uuid is cut."
  [clip id n]
  (or (some->> (node/source n) (symbol-name clip))
      (:name n)
      (if (keyword? id) (subs (str id) 1) (subs (str id) 0 8))))

(defn uses
  "How many nodes in the document place symbol `sid`."
  [clip sid]
  (count (for [[_ sym] (:symbols clip)
               [_ n] (:nodes sym)
               :when (= sid (node/source n))]
           n)))

(defn frames
  "A symbol's length. Read off the symbol, never copied beside it."
  [clip sid]
  (:frames (symbol clip sid)))

(defn placed-frame
  "Which frame of which symbol instance `n` shows on its own frame `f`, as
  `{:symbol :frame}`, or nil. See `node/placed-frame`."
  [clip n f]
  (some-> (node/placed-frame n f (frames clip (node/source n)))
          (update :frame js/Math.floor)))

(defn stage
  "A symbol's stage as `[width height]`: its own, or the clip's where it has none.
  Absent rather than copied in at creation, so a symbol nobody has sized follows
  the project's size when that changes."
  [clip sid]
  (let [sym (symbol clip sid)]
    [(or (:width sym) (:width clip)) (or (:height sym) (:height clip))]))

(defn update-symbol
  "Apply f to one symbol in place."
  [clip sid f & args]
  (apply update-in clip [:symbols sid] f args))

(defn places
  "The ids of the symbols `sid` places, directly."
  [clip sid]
  (into #{} (mapcat node/sources) (vals (:nodes (symbol clip sid)))))

(defn contains-symbol?
  "Whether `inner` is `outer` or is placed anywhere inside it. Placing `outer`
  into `inner` when this is true is a cycle."
  [clip outer inner]
  (let [seen (volatile! #{})]
    (letfn [(walk [sid]
              (or (= sid inner)
                  (when-not (@seen sid)
                    (vswap! seen conj sid)
                    (some walk (places clip sid)))))]
      (boolean (walk outer)))))

(defn unplaced
  "The symbols no other symbol places, sorted by id. What to open when a
  document is opened."
  [clip]
  (let [placed (into #{} (mapcat #(places clip %)) (keys (:symbols clip)))]
    (vec (sort-by str (remove placed (keys (:symbols clip)))))))

(defn- longest-unplaced
  "The longest symbol nothing else places, ties broken by id, and never a
  tracing symbol: that is footage nobody has placed yet, as long as its take."
  [clip]
  (first (sort-by (fn [sid] [(- (or (frames clip sid) 0)) (str sid)])
                  (remove #(trace? (symbol clip %)) (unplaced clip)))))

(defn opens-on
  "The symbol a document opens on: its `:root`.

  IT IS STORED, NOT WORKED OUT. It used to be the longest unplaced symbol, on
  the theory that the one containing everything else is always that. A take
  disproves it: imported footage is a symbol a thousand frames long, and the
  moment its instance is deleted, or the drop lands somewhere other than the
  root, nothing places it and it outranks a 120-frame `:main`, and the document
  opened on the take.

  A document with no `:root` — a demo, a fixture — still gets the old answer."
  [clip]
  (let [root (:root clip)]
    (if (contains? (:symbols clip) root) root (longest-unplaced clip))))

(def blank-settings
  "What a new document is before anyone says otherwise. 12fps because the rough
  look is the point; four seconds is long enough to key something into and short
  enough to scrub by hand."
  {:name "untitled" :fps 12 :seconds 4 :width 320 :height 200})

(defn blank
  "A new, empty document: one symbol, and nothing in it.

  A SYMBOL IS BORN EMPTY. It used to be born holding a lane, because a lane was
  the only place temporal content could go; now the symbol itself is the
  container — see `symbol/children` — so there is nothing to invent and the
  first drop into a symbol is the same operation as the second.

  The tracking maps are ABSENT rather than empty, because `leaf/leaves` writes no
  leaf for an empty one and so cannot bring it back: a blank document that opened
  as a different map than it saved from is exactly the round trip that namespace
  promises not to have. Nothing drawn by hand has them either — the demo scene
  and the swarm carry no `:subjects` — so every reader already reads absence as
  none, and `clip-keys` says which fields MAY be here, not which must.

  `settings` overrides any of `blank-settings`. `:palette` names the preset it
  draws with, and that one is the project's only palette until more are added."
  ([] (blank {}))
  ([settings]
   (let [{:keys [fps seconds width height palette] :as s} (merge blank-settings (into {} (remove (comp nil? val)) settings))
         presets (into {} (map (juxt :id identity)) pal/presets)
         palette (if (contains? presets palette) palette pal/default-id)]
     {:name (:name s)
      :fps fps
      :width width :height height
      :palettes (select-keys presets [palette])
      :default-palette palette
      :root :main
      :symbols {:main {:id :main :frames (max 1 (js/Math.round (* seconds fps))) :nodes {}}}})))

(defn- op-path [op] (let [n (:node op)] (if (vector? n) n [n])))

(defn- own-span
  "Where in `ops` the symbol at row path `path` is drawn: the end of its layer
  when it has one, and the first and last of its own ops."
  [ops path]
  (let [own?  #(and (< (count path) (count (op-path %)))
                    (= path (subvec (op-path %) 0 (count path))))
        begin (first (keep-indexed #(when (and (= :begin (:kind %2)) (= path (op-path %2))) %1) ops))
        end   (when begin
                (reduce (fn [depth i]
                          (case (:kind (ops i))
                            :begin (inc depth)
                            :end   (if (= 1 depth) (reduced i) (dec depth))
                            depth))
                        0 (range begin (count ops))))
        mine  (keep-indexed #(when (own? %2) %1) ops)]
    {:end (when (integer? end) end) :from (first mine) :to (last mine)}))

(defn- insert-at [ops i & more] (into (into (subvec ops 0 i) more) (subvec ops i)))

(defn atop
  "`ops` with `op` drawn on top of what the symbol at row path `path` draws —
  in ITS stacking context, where a shape added to it would land, so what is
  above that symbol stays above. At the end when it drew nothing."
  [ops path op]
  (let [ops (vec ops)
        {:keys [end to]} (own-span ops path)]
    (cond end  (insert-at ops end op)
          to   (insert-at ops (inc to) op)
          :else (conj ops op))))

(defn in-layer
  "`ops` with `op` drawn last INSIDE the symbol at row path `path` — in its
  layer, so a knockout clears only what that symbol drew, as one saved there
  would. A symbol that has no layer yet is given one around its ops. `ops`
  unchanged when that symbol drew nothing."
  [ops path op]
  (let [ops (vec ops)
        {:keys [end from to]} (own-span ops path)]
    (cond
      end   (insert-at ops end op)
      from  (-> ops
                (insert-at (inc to) op {:kind :end :node path})
                (insert-at from {:kind :begin :node path}))
      :else ops)))

(defn- transform-op
  "Put a symbol's already resolved mark into its instance's parent space. Its
  name becomes its path of instances down to it, the path its timeline row has."
  [op m path]
  (let [at (fn [x y] [(+ (* (aget m 0) x) (* (aget m 2) y) (aget m 4))
                       (+ (* (aget m 1) x) (* (aget m 3) y) (aget m 5))])
        scale (node/mean-scale m)
        n (:node op)
        op (assoc op :node (if (vector? n) (into path n) (conj path n)))]
    (case (:kind op)
      :poly (let [out (js/Float64Array. (.-length (:pts op)))]
              (dotimes [i (:n op)]
                (let [[x y] (at (aget (:pts op) (* 2 i))
                                (aget (:pts op) (inc (* 2 i))))]
                  (aset out (* 2 i) x)
                  (aset out (inc (* 2 i)) y)))
              (assoc op :pts out))
      :disc (let [[x y] (at (:cx op) (:cy op))]
              (assoc op :cx x :cy y :r (* scale (:r op))))
      :rect (let [[x y] (at (:cx op) (:cy op))]
              (assoc op :cx x :cy y :size (* scale (:size op))))
      :trace (assoc op :m (node/mul! (node/mat) m (:m op)))
      op)))

(defn- trace-op
  "The one op an instance of tracing symbol `sym` makes, showing its frame `frame`
  at world `m`, from node `id` of symbol `sid`.

  NOT A PICTURE OP. Nothing indexed can show a photo, so the raster refuses this
  kind and the player hands it to `ui/tracing` instead; and the resolver makes one
  only when asked with `:tracing?`, which only the stage does. An export, a
  symbol's centre and a thumbnail never ask, so a reference cannot reach the
  picture by any path that forgets to filter it.

  `:layer` is what the on/off switch for one layer is keyed by: the symbol the
  placement is in and its id, so a face's plate is one layer wherever the face
  is placed."
  [sym frame m sid id]
  {:kind  :trace
   :node  id
   :layer [sid id]
   :media (:media sym)
   :frame frame
   :size  [(:width sym) (:height sym)]
   :m     (js/Float64Array.from m)})

(defprotocol IActivePalette
  (active-palette [this]
    "The palette selected by this resolver's most recently resolved frame."))

(defn- channel-value [store selection frame]
  (cond
    (nil? selection) nil
    (and (map? selection) (contains? selection :animated?))
    (ch/value-at selection frame store)
    :else selection))

(defn- own-palette
  "The palette symbol `owner` itself chooses at `frame`, or nil when it chooses
  none and takes what it is in: its palette lane where a segment covers the
  frame, else its own `:palette` (which an instance's palette stands in for).
  A lane's `inherit` end means the symbol's own palette when it has one, and
  stays `inherit` — whatever the frame is in without this lane — when not.
  A palette lane has meaningful uncovered time: ordinary held channels clamp to
  their first key before it, which here would erase the gap before the first
  segment."
  [clip store owner frame]
  (let [own (channel-value store (:palette owner) frame)
        fill #(if (and own (= pal/inherit %)) own %)
        settle (fn [choice] (if (map? choice) (-> choice (update :from fill) (update :to fill)) (fill choice)))
        track (:palette-channel owner)]
    (or (when-let [track-symbol (some->> (:palette-track owner) (symbol clip))]
          (let [palette-clip (first
                              (filter (fn [n]
                                        (let [[a b] (node/placed-span n)]
                                          (and a (<= a frame) (< frame b))))
                                      (symbol/children (:nodes track-symbol))))
                palette-symbol (some-> palette-clip node/source (#(symbol clip %)))
                choice (get-in palette-clip [:channels [:palette]])
                start (some-> palette-clip node/placed-span first)]
            (or (when (and choice start)
                  (settle (channel-value store choice (- frame start))))
                (when (= :palette (:type palette-symbol))
                  (:palette-ref palette-symbol)))))
        (if-let [ks (:keys track)]
          (some->> (keys ks) (filter #(<= % frame)) sort last (get ks))
          (channel-value store track frame))
        own)))

(defn- settle
  "`choice` with an `inherit` end standing for `under`, what the frame would be
  in without it."
  [choice under]
  (let [under (if (map? under) (:from under) under)
        fill #(if (= pal/inherit %) under %)]
    (cond
      (nil? choice) under
      (map? choice) (-> choice (update :from fill) (update :to fill))
      :else (fill choice))))

(defn palette-at
  "The palette symbol `owner` chooses at `frame`, with what it does not choose
  standing for `inherited`, then `default`: an id, or `{:from :to :t}` while
  its palette lane blends."
  [clip store default owner frame inherited]
  (settle (own-palette clip store owner frame) (or inherited default)))

(defn frame-palette
  "THE ONE PALETTE A FRAME IS IN, from what each symbol drawn in it chooses:
  `choices` is `[depth choice]` in draw order, the open symbol at depth 0.
  The SHALLOWEST choice wins — a palette set on the open symbol overrides
  anything inside it — and between two at one depth the one drawn last, on
  top. What the winner leaves to `inherit` is the next choice down, and under
  them all the project's."
  [default choices]
  (reduce (fn [under [_ choice]] (settle choice under))
          default
          ;; Deepest first, so the shallowest settles last; `sort-by` is
          ;; stable, so at one depth the one drawn last does.
          (sort-by (comp - first) choices)))

(defn palette-uses
  "Every palette symbol `sid` and what it places choose, and where:
  `{:palette :sid :via}`, `:via` being `:symbol` for a symbol's own, `:lane` for
  its palette lane and `:instance` for an instance set to one. Outermost first,
  each once. A palette nothing chooses is the one inherited, so is not here."
  [clip sid]
  (loop [todo [sid] seen #{} out []]
    (if-let [s (first todo)]
      (if (contains? seen s)
        (recur (rest todo) seen out)
        (let [sym   (symbol clip s)
              lane  (symbol clip (:palette-track sym))
              use   (fn [via x] (map (fn [p] {:palette p :sid s :via via}) (pal/choices x)))
              nodes (vals (:nodes sym))]
          (recur (concat (rest todo) (mapcat node/sources (filter #(= :instance (:kind %)) nodes)))
                 (conj seen s)
                 (into out (distinct)
                       (concat (use :symbol (:palette sym))
                               (use :lane (:palette-channel sym))
                               (mapcat (fn [n]
                                         (concat (use :lane (:palette-ref (some->> (node/source n) (symbol clip))))
                                                 (use :lane (get-in n [:channels [:palette]]))))
                                       (vals (:nodes lane)))
                               (mapcat #(use :instance (:palette %)) nodes))))))
      (vec (distinct out)))))

(defn resolver
  "Resolve an output frame, selecting native content at each symbol boundary.
  Every instance owns its cursors and buffers. The IResolver queries return
  native node frames and world matrices for the last rendered output frame."
  [clip sid store palette opts]
   (let [context? (and (map? palette) (contains? palette :palettes))
         active-palette-state (atom (:default palette))
         ;; What each symbol drawn this frame chooses, as `[depth choice]`.
         chosen (volatile! [])]
    (letfn [(build [sid chain pose-tracks root?]
             (when (some #{sid} chain)
               (throw (ex-info "symbol cycle" {:chain (conj chain sid)})))
             (let [sym (or (symbol clip sid)
                           (throw (ex-info "an instance names a missing symbol" {:symbol sid})))
                   depth (count chain)
                   nodes (:nodes sym)
                   rank (symbol/draw-rank nodes (symbol/order nodes))
                   ids (sort-by rank (keys nodes))
                   own (symbol/resolver sym store (if context? pal/slot palette)
                                        (assoc opts :pose-tracks pose-tracks))
                   ;; Each cel owns its source resolver and mutable buffers. A
                   ;; tracing symbol has nothing to resolve: it is one op.
                   children (into {}
                                  (for [[id n] nodes
                                        :when (= :instance (:kind n))
                                        child (sort-by str (node/sources n))
                                        :when (not (trace? (symbol clip child)))]
                                    [[id child] (build child (conj chain sid)
                                                       (get-in n [:playback :tracks]) false)]))
                   ;; The instances that were on the last frame, and WHICH
                   ;; drawing each was showing — a row path is read back through
                   ;; the child that was actually resolved, not the only one
                   ;; there used to be. Their resolvers still hold the frame
                   ;; before whenever they were not on.
                   entered (volatile! {})
                   ;; A symbol that knocks out is drawn into a layer of its own,
                   ;; so what it clears is only ever its own.
                   layered? (symbol/knocks? nodes)
                   ;; Each symbol drawn says which palette it chooses, if any;
                   ;; the root, once all are drawn, makes the frame's ONE
                   ;; palette of them. Shapes draw slots, so nothing drawn
                   ;; depends on which. An instance's palette stands in for the
                   ;; symbol's own, so its lane still plays.
                   step (fn [f pre override]
                          (when root? (vreset! chosen []))
                          (when context?
                            (when-let [c (own-palette clip store
                                                      (cond-> sym override (assoc :palette override))
                                                      (js/Math.floor f))]
                              (vswap! chosen conj [depth c])))
                          (vreset! entered {})
                          (let [by-id (into {} (map (juxt :node identity))
                                            (own (js/Math.floor f) (js/Math.floor pre)))]
                            (cond-> (into (if layered? [{:kind :begin :node []}] [])
                                  (mapcat
                                   (fn [id]
                                     (let [n (get nodes id)]
                                       (if (= :instance (:kind n))
                                         (let [m     (symbol/world-of own id)
                                               local (symbol/frame-of own id)
                                               prior (symbol/pre-frame-of own id)
                                               length (frames clip (node/source n))
                                               shown (when (and m (number? local))
                                                       (placed-frame clip n local))
                                               frame (:frame shown)]
                                           (cond
                                             (not (and frame (<= 0 frame) (< frame length)))
                                             []

                                             (trace? (symbol clip (:symbol shown)))
                                             (when (:tracing? opts)
                                               [(trace-op (symbol clip (:symbol shown)) frame m sid id)])

                                             :else
                                             (do (vswap! entered assoc id (:symbol shown))
                                                 (map #(transform-op % m [id])
                                                      ((get children [id (:symbol shown)])
                                                       frame
                                                       ;; The slot's interval crosses the
                                                       ;; boundary by being MAPPED, not
                                                       ;; carried: the previous slot's
                                                       ;; frame goes through the same
                                                       ;; placement and retime as this
                                                       ;; one, so the gap comes out in the
                                                       ;; child's frames and at the child's
                                                       ;; rate. An instance appearing for
                                                       ;; the first time on this slot has no
                                                       ;; previous frame, and so no gap.
                                                       (or (:frame (when (number? prior)
                                                                     (placed-frame clip n prior)))
                                                           (dec frame))
                                                       (:palette n))))))
                                         (when-let [op (get by-id id)] [op]))))
                                   ids))
                              layered? (conj {:kind :end :node []}))))]
               (reify
                 IFn
                 (-invoke [_ f] (step f (dec f) nil))
                 (-invoke [_ f pre] (step f pre nil))
                 (-invoke [_ f pre override] (step f pre override))
                 symbol/IResolver
                 (world-of [_ [id & more]]
                   (if more
                     (when-let [w (and (contains? @entered id)
                                       (symbol/world-of (get children [id (get @entered id)])
                                                        (vec more)))]
                       (node/mul! (node/mat) (symbol/world-of own id) w))
                     (symbol/world-of own id)))
                 (frame-of [_ [id & more]]
                   (if more
                     (when (contains? @entered id)
                       (symbol/frame-of (get children [id (get @entered id)]) (vec more)))
                     (symbol/frame-of own id)))
                 (pre-frame-of [_ [id & more]]
                   (if more
                     (when (contains? @entered id)
                       (symbol/pre-frame-of (get children [id (get @entered id)]) (vec more)))
                     (symbol/pre-frame-of own id))))))]
     (let [r (build sid [] nil true)]
       (reify
         IFn
         ;; `pre` is the frame before, so a child placed at a speed is handed
         ;; the interval `(pre, f]` its preserve-snap may reach back into.
         (-invoke [_ f]
           (let [ops (r f (dec f))]
             (when context?
               (reset! active-palette-state (frame-palette (:default palette) @chosen)))
             ops))
         symbol/IResolver
         (world-of [_ path] (symbol/world-of r path))
         (frame-of [_ path] (symbol/frame-of r path))
         (pre-frame-of [_ path] (symbol/pre-frame-of r path))
         IActivePalette
         (active-palette [_] @active-palette-state))))))

(defn center
  "The middle of everything symbol `sid` draws, over all its frames, in its own
  coordinates. ALL frames rather than the first, so a symbol whose drawing
  enters late, or travels, still has its middle where the drawing is. A symbol
  that draws nothing gets the STAGE's middle, which is where a drawing made into
  it will be, because drawings are made on the stage.

  WHERE A DROP LANDS, AND WHAT THE INSTANCE TURNS ABOUT. `place-symbol` puts this
  point under the pointer and stores it as the instance's `[:xform :pivot]`, and
  `ui/drag`'s ghost draws the cross there so a drop lands where it was aimed —
  one point, one meaning, three uses.

  IT IS A DEFAULT AND NOT A CACHE, which is the distinction the stored anchor got
  wrong. A pivot written here is a CHOICE made on the instance's behalf at the
  moment it is placed, the same way `paint/centred` chooses a drawing's origin
  when it is drawn; editing the symbol afterwards does not revise either, and the
  cross is draggable so neither is a trap. What the anchor got wrong was being
  invisible and unmovable, not being stored."
  [clip store sid]
  (let [resolve (resolver clip sid store pal/index-of {})
        bounds  (fn [[x0 y0 x1 y1 :as b] x y]
                  (if b [(min x0 x) (min y0 y) (max x1 x) (max y1 y)] [x y x y]))
        [x0 y0 x1 y1]
        (reduce
         (fn [b {:keys [kind pts n cx cy r size]}]
           (case kind
             :poly (reduce (fn [b i] (bounds b (aget pts (* 2 i)) (aget pts (inc (* 2 i)))))
                           b (range n))
             :disc (-> b (bounds (- cx r) (- cy r)) (bounds (+ cx r) (+ cy r)))
             :rect (let [h (/ size 2)] (-> b (bounds (- cx h) (- cy h)) (bounds (+ cx h) (+ cy h))))
             b))
         nil
         (mapcat resolve (range (frames clip sid))))]
    (if x0
      [(/ (+ x0 x1) 2) (/ (+ y0 y1) 2)]
      (mapv #(/ % 2) (stage clip sid)))))

(defn place-symbol
  "An instance of symbol `sid`, inside symbol `host`, at `frame` of `host`.

  THE MIDDLE GOES UNDER THE POINTER, AND IS WHAT THE INSTANCE TURNS ABOUT.
  `center` says where the symbol's drawing sits in its own coordinates; `pos` is
  set so that point lands on `point`, a stage pixel — without one, a drop on the
  timeline, the drawing stays where it was drawn — and the same point is stored as
  the instance's `[:xform :pivot]`, so a turn or a scale happens about the middle
  of the drawing rather than about the symbol's origin.

  WITHOUT THAT PIVOT AN INSTANCE TURNS ABOUT THE CORNER OF THE STAGE. A symbol's
  origin is the stage's, because that is where its contents were drawn, so the
  middle of a drawing inside one is typically a hundred-odd pixels away from it on
  a 320x200 stage. `node/local!` composes about the pivot, so this one stored
  point is the difference between spinning in place and orbiting the top-left
  corner. See `domain/gesture`.

  THE UUID IS AN ARGUMENT. An instance's identity is the key it has in the node
  map — it is what `:linked-to`, an export target and a saved leaf all name — so
  generating one in here would make this function's result depend on when it was
  called, and this namespace is the pure one.

  The instance's own time starts where it was dropped: `:at frame` means frame 0
  of the symbol plays on `frame` of `host`, which is what dragging something onto
  a playhead is asking for. Its `:span` is in its OWN frames — the whole symbol,
  0 to its length at `speed` — wherever it was dropped; see `node/placed-span`.
  `speed` is 1 unless the symbol's frames are footage's at another rate, and 0
  for a held cel, which shows its symbol's first frame and so starts one frame
  long, as `span/held` does.

  Refused, returning the clip unchanged, when it would make a cycle: a symbol
  cannot be placed inside itself or inside anything it places."
  ([clip store host sid frame uuid point] (place-symbol clip store host sid frame uuid point 1))
  ([clip store host sid frame uuid point speed]
  (let [target (symbol clip sid)
        end    (frames clip host)]
    (if (or (nil? target) (nil? end) (nil? frame) (neg? frame) (>= frame end)
            (contains-symbol? clip sid host))
      clip
      (let [middle (center clip store sid)]
        (update-symbol
         clip host assoc-in [:nodes uuid]
         {:id uuid
          :kind :instance
          :parent nil
          ;; Lexicographic draw order, as `domain/paint` does it: an instance made
          ;; later sits above one made earlier, and neither has to renumber.
          :z (str "z" (js/Date.now) "-" (name sid))
          :span [0 (if (pos? speed) (js/Math.ceil (/ (:frames target) speed)) 1)]
          :time {:mode :map :at frame :rate 1}
          :source {:symbol sid}
          :playback {:in 0 :speed speed :end :stop}
          :channels {[:xform :pos] {:animated? false
                                    :value (if point (mapv - point middle) [0 0])}
                     [:xform :pivot] {:animated? false :value middle}}}))))))

(defn place-sound
  "Place a sound at host frame `frame`. Its span uses `source`'s fps when
  supplied, otherwise the host's. `rate` is a deliberate playback speed."
  [clip host source label length rate frame uuid]
  (let [end (frames clip host)]
    (if (or (nil? end) (nil? frame) (neg? frame) (>= frame end))
      clip
      (update-symbol
       clip host assoc-in [:nodes uuid]
       {:id uuid
        :name label
        :kind :audio
        :parent nil
        :z (str "z" (js/Date.now) "-sound")
        :source source
        :span [0 (max 1 length)]
        :time {:mode :map :at frame :rate rate}}))))

(defn fresh-id
  "The first `:symbol-N` the clip does not already hold. Readable because an id
  shows up in saved leaf paths, and deterministic because this namespace is pure."
  [clip]
  (first (remove (:symbols clip) (map #(keyword (str "symbol-" %)) (iterate inc 1)))))

(defn new-symbol
  "A new, empty symbol `sid`, placed inside `host` at `frame` and running to the
  end of it. Placed at the origin, so whatever is drawn into it lands where it was
  drawn until the instance is moved."
  [clip host sid frame uuid]
  (let [end (frames clip host)]
    (if (or (nil? end) (symbol clip sid) (nil? frame) (neg? frame) (>= frame end))
      clip
      (-> clip
          (assoc-in [:symbols sid] {:id sid :name (name sid)
                                    :frames (- end frame) :nodes {}})
          (place-symbol nil host sid frame uuid nil)))))

(defn free-id
  "`wanted`, or the first `wanted-2`, `wanted-3`… `taken?` does not claim.
  Keeps the namespace, so `:sym/face` becomes `:sym/face-2`."
  [taken? wanted]
  (first (remove taken?
                 (cons wanted
                       (map #(keyword (namespace wanted) (str (name wanted) "-" %))
                            (iterate inc 2))))))

(defn conflicts
  "Every hand correction in the document that its base has outgrown, as
  `[{:symbol :node :channel :id :why}]`.

  SEPARATE FROM `problems` on purpose. A conflict is a document a person still
  has to make a decision about — a regeneration changed the topology under a
  correction that was right when it was made — and not a reason the document
  will not load. Nothing is dropped and nothing is misapplied meanwhile: the
  layer stays where it is, the picture is the base, and this is the list a view
  offers to resolve."
  [clip]
  (vec (for [[sid sym] (:symbols clip)
             [id n] (:nodes sym)
             [prop c] (:channels n)
             {:keys [why] :as x} (ch/conflicts c)]
         (assoc (select-keys x [:id]) :symbol sid :node id :channel prop :why why))))

(defn problems
  "Human-readable reasons this clip will not evaluate or save."
  [clip]
  (vec
   (concat
    (for [k (remove clip-keys (keys clip))]
      (str "clip has a field with no leaf to save it in: " (pr-str k)))
    (when-not (map? (:symbols clip))
      [":symbols must be a map of id -> symbol"])
    (when (and (contains? clip :analyses) (not (map? (:analyses clip))))
      [":analyses must be a map of analysis id -> analysis"])
    (for [[id analysis] (:analyses clip)
          :when (not= id (:id analysis))]
      (str "analysis under key " (pr-str id) " has :id " (pr-str (:id analysis))))
    (for [[id subject] (:subjects clip)
          :when (not (contains? (:analyses clip) (:analysis subject)))]
      (str "subject " (pr-str id) " names missing analysis "
           (pr-str (:analysis subject))))
    (for [[id subject] (:subjects clip)
          :when (not (keyword? (:source-subject subject)))]
      (str "subject " (pr-str id) " has no source subject"))
    (when (and (contains? clip :palettes) (not (map? (:palettes clip))))
      [":palettes must be a map of id -> palette"])
    (when (and (contains? clip :default-palette) (map? (:palettes clip))
               (not (contains? (:palettes clip) (:default-palette clip))))
      [":default-palette must name a project palette"])
    (when (and (contains? clip :root) (map? (:symbols clip))
               (not (contains? (:symbols clip) (:root clip))))
      [(str ":root names missing symbol " (pr-str (:root clip)))])
    (for [[id p] (:palettes clip)
          :when (or (not= id (:id p)) (not (pal/valid-palette? p)))]
      (str "palette " (pr-str id) " is invalid or has a different :id"))
    (when-not (or (nil? (:fps clip)) (and (number? (:fps clip)) (pos? (:fps clip))))
      [(str ":fps is " (pr-str (:fps clip)) " — a rate is a positive number")])
    (for [[id sym] (:symbols clip)
          :when   (not= id (:id sym))]
      (str "symbol under key " (pr-str id) " has :id " (pr-str (:id sym))))
    (for [[id sym] (:symbols clip)
          p       (symbol/problems sym)]
      (str "symbol " (pr-str id) ": " p))
    (for [[sid sym] (:symbols clip)
          [id n] (:nodes sym)
          :when (= :instance (:kind n))
          missing (remove (:symbols clip) (node/sources n))]
      (str "symbol " (pr-str sid) " instance " (pr-str id)
           " names missing symbol " (pr-str missing)))
    ;; THE INVARIANT `place-symbol` AND `ui/drag` ALREADY ENFORCE, stated here so
    ;; that every command is checked against it rather than the two that remember
    ;; to ask. A symbol placed inside itself, or inside anything it places, has no
    ;; finite expansion: `build` above and `nest/audio-tracks` both walk instances
    ;; and both throw on the way round. Paste reached this function without it and
    ;; wrote a document that saved, loaded, and only then threw — which is the one
    ;; outcome `problems` exists to make impossible.
    (for [[sid sym] (:symbols clip)
          [id n] (:nodes sym)
          :when (= :instance (:kind n))
          src (node/sources n)
          ;; A source that does not exist is the rule above's to report, not this
          ;; one's, so it does not get named twice.
          :when (and (contains? (:symbols clip) src)
                     (contains-symbol? clip src sid))]
      (str "symbol " (pr-str sid) " instance " (pr-str id) " places "
           (pr-str src) (if (= src sid) ", which is itself" ", which contains it")))
    ;; Pose tracks belong to this cel's single source symbol.
    (for [[sid sym] (:symbols clip)
          [id n] (:nodes sym)
          :when (= :instance (:kind n))
          :let [targets (keep #(get-in clip [:symbols %]) (node/sources n))
                active (filter (fn [node]
                                 (some :pose-sampled? (vals (:channels node))))
                               (mapcat #(vals (:nodes %)) targets))
                groups (set (concat
                             (map #(or (:pose-group %) (:id %)) active)
                             (map #(vector :node (:id %)) active)))]
          p (pose/problems (get-in n [:playback :tracks])
                           (apply max 0 (keep :frames targets)) groups)]
      (str "symbol " (pr-str sid) " instance " (pr-str id) ": " p))
    (for [[sid sym] (:symbols clip)
          [id n] (:nodes sym)
          :when (and (= :audio (:kind n)) (:linked-to n)
                     (not (contains? (:nodes sym) (:linked-to n))))]
      (str "symbol " (pr-str sid) " audio " (pr-str id)
           " links to missing node " (pr-str (:linked-to n))))
    (feature/problems clip))))
